/**
 * Phase 1b — Trust/company attribution for means test, tax, and cashflow.
 * Entity-owned assets are excluded from personal paths; apportioned totals merge into calculateFinancials.
 * Ported verbatim from frontend/src/calculations/structureAttribution.js.
 */
import {
    getEntityIdFromOwnership,
    isEntityOwnership,
    getApplicantsCombinedApportionmentPercent,
} from './constants/clientStructureTypes.ts';
import { COMPANY_TAX_RATE, MINIMUM_PENSION_DRAWDOWN_RATES } from './constants/ratesAndThresholds.ts';
import { calculateAnnualLoanRepayment, isLiquidAssetType, resolveLiabilityEntityId } from './support/structureLiabilitiesUtils.ts';
import { isEntityPrincipalHomeExempt } from './entityHomeRules.ts';
import type { FactFind, FactFindAsset } from './factFindTypes.ts';

const FUNERAL_BOND_EXEMPT_LIMIT_PER_PERSON = 15000;
const DEEMING_FINANCIAL_TYPES = new Set(['bankAccounts', 'termDeposits', 'shares', 'investmentBonds', 'accountBasedPensions', 'loansToOthers']);

interface Pool {
    assessableAssets: number;
    assessableAssetsNoHome: number;
    financialForDeeming: number;
    liquidAssetsGross: number;
    liabilitiesGross: number;
    liabilitiesNoHome: number;
    loanRepaymentsAnnual: number;
    investmentPropertyRental: number;
    assessableIncomeFromPensions: number;
    funeralBondGross: number;
    investmentIncomeCash: number;
    rentalExpenses: number;
    incomeItems: Array<{ label: string; taxable: number; deductions: number; franking: number; cash: number }>;
}

function emptyPool(): Pool {
    return {
        assessableAssets: 0,
        assessableAssetsNoHome: 0,
        financialForDeeming: 0,
        liquidAssetsGross: 0,
        liabilitiesGross: 0,
        liabilitiesNoHome: 0,
        loanRepaymentsAnnual: 0,
        investmentPropertyRental: 0,
        assessableIncomeFromPensions: 0,
        funeralBondGross: 0,
        investmentIncomeCash: 0,
        rentalExpenses: 0,
        incomeItems: [],
    };
}

function getAssessableValue(type: string, asset: FactFindAsset): number {
    const value = asset.value || 0;
    if (type === 'annuities' && asset.assessableAsset !== undefined) {
        return asset.assessableAsset || 0;
    }
    return value;
}

function getMinimumPensionDrawdownRate(age: number): number {
    if (!age) return 0;
    const bracket = MINIMUM_PENSION_DRAWDOWN_RATES.find((b) => age <= b.age);
    return bracket ? bracket.rate : 0;
}

function recordIncomeItem(pool: Pool, label: string, taxable: number, deductions: number, franking: number, cash: number): void {
    pool.incomeItems.push({ label, taxable, deductions, franking, cash });
    pool.investmentIncomeCash += cash;
    pool.rentalExpenses += deductions;
}

export function computeStructureAttribution(
    factFind: FactFind,
    options: { isPartnered?: boolean; clientAge?: number | null; partnerAge?: number | null; strategyOptions?: any } = {},
): any {
    const { isPartnered, clientAge, partnerAge, strategyOptions = {} } = options;
    const structures = factFind.structures || [];
    if (!structures.length) {
        return null;
    }

    const principalHomeExempt = isEntityPrincipalHomeExempt(factFind, strategyOptions);
    const entityHomeEntityId = principalHomeExempt && factFind.home?.entityId ? factFind.home.entityId : null;

    const pools: Record<string, Pool> = {};
    structures.forEach((s) => {
        pools[String(s.id)] = emptyPool();
    });

    const addToPool = (entityId: string | null, fn: (pool: Pool) => void) => {
        if (!entityId || entityId === '__unsure__' || !pools[entityId]) return;
        fn(pools[entityId]!);
    };

    Object.entries(factFind.assets || {}).forEach(([type, assetList]) => {
        (assetList || []).forEach((asset) => {
            if (!isEntityOwnership(asset.ownership)) return;
            const entityId = getEntityIdFromOwnership(asset.ownership);
            addToPool(entityId, (pool) => {
                const value = asset.value || 0;
                const assessableValue = getAssessableValue(type, asset);

                if (type === 'radPaid' || type === 'prepaidFuneral') return;

                if (type === 'funeralBonds') {
                    pool.funeralBondGross += value;
                    return;
                }

                const isPre2015ABP = type === 'accountBasedPensions' && asset.commencementDateOption === 'pre-2015';
                const isFinancialForDeeming =
                    !isPre2015ABP &&
                    (DEEMING_FINANCIAL_TYPES.has(type) || (type === 'annuities' && asset.annuityType !== 'lifetime'));

                if (isFinancialForDeeming) {
                    pool.financialForDeeming += value;
                }
                if (isLiquidAssetType(type)) {
                    pool.liquidAssetsGross += value;
                }

                if (type === 'investmentProperty') {
                    const rent = asset.rent || 0;
                    const expenses = asset.expenses || 0;
                    const netRental = Math.max(0, rent - expenses);
                    pool.investmentPropertyRental += netRental;
                    pool.assessableAssets += assessableValue;
                    pool.assessableAssetsNoHome += assessableValue;
                    recordIncomeItem(pool, asset.description || 'Investment Property', rent, expenses, 0, rent);
                    return;
                }

                if (type === 'home') {
                    const skipPrincipalHome = principalHomeExempt && entityId === entityHomeEntityId;
                    if (!skipPrincipalHome && !strategyOptions.rentHome && !strategyOptions.downsizeHome) {
                        pool.assessableAssets += assessableValue;
                    }
                    return;
                }

                if (type !== 'funeralBonds') {
                    pool.assessableAssets += assessableValue;
                    pool.assessableAssetsNoHome += assessableValue;
                }

                if (type === 'shares') {
                    const dividend = value * ((asset.incomeRate || 0) / 100);
                    const frankingPercent = (asset.frankingCredit || 0) / 100;
                    const frankedAmount = dividend * frankingPercent;
                    const frankingCredit = frankedAmount * (COMPANY_TAX_RATE / (1 - COMPANY_TAX_RATE));
                    const taxable = dividend + frankingCredit;
                    recordIncomeItem(pool, asset.description || 'Shares', taxable, 0, frankingCredit, dividend);
                } else if (type === 'bankAccounts') {
                    const rate = (asset.incomeRate || 0) / 100;
                    const interest = value * rate;
                    recordIncomeItem(pool, asset.description || 'Bank Interest', interest, 0, 0, interest);
                } else if (type === 'termDeposits') {
                    const rate = (asset.incomeRate || 0) / 100;
                    const interest = value * rate;
                    const label = asset.description || 'Term Deposit';
                    recordIncomeItem(pool, label, interest, 0, 0, interest);
                } else if (type === 'accountBasedPensions' && !isPre2015ABP) {
                    const ownerAge = clientAge || partnerAge || 65;
                    const minDrawdown = value * getMinimumPensionDrawdownRate(ownerAge);
                    const payment = value > 0 ? asset.income || 0 : 0;
                    const cash = Math.max(payment, minDrawdown);
                    const taxable = ownerAge >= 60 ? 0 : cash;
                    recordIncomeItem(pool, asset.description || 'ABP', taxable, 0, 0, cash);
                } else if (type === 'annuities' || type === 'other') {
                    let cash: number;
                    let taxable: number;
                    if (type === 'annuities') {
                        cash = asset.annualPayment || 0;
                        taxable = asset.assessableIncome || cash;
                    } else {
                        const rate = asset.income ? asset.income / value : 0;
                        cash = value * rate;
                        taxable = cash;
                    }
                    recordIncomeItem(pool, asset.description || type, taxable, 0, 0, cash);
                }
            });
        });
    });

    const home = factFind.home || {};
    const homeValue = home.value || 0;
    if (homeValue > 0 && home.ownership === 'other_entity' && home.entityId) {
        addToPool(home.entityId, (pool) => {
            if (!(principalHomeExempt && home.entityId === entityHomeEntityId)) {
                pool.assessableAssets += homeValue;
            }
        });
    }

    Object.values(factFind.liabilities || {}).forEach((list) => {
        (list || []).forEach((liability) => {
            const entityId = resolveLiabilityEntityId(liability, factFind);
            if (!entityId) return;
            const balance = liability.balance || 0;
            addToPool(entityId, (pool) => {
                pool.liabilitiesGross += balance;
                pool.loanRepaymentsAnnual += calculateAnnualLoanRepayment(liability);
                const securedOnHome =
                    liability.type === 'mortgages' &&
                    (liability.assetLink === 'home' ||
                        (factFind.home?.ownership === 'other_entity' && factFind.home?.entityId === entityId && !liability.assetLink));
                if (!securedOnHome) {
                    pool.liabilitiesNoHome += balance;
                }
                if (liability.type === 'mortgages' && liability.assetLink && liability.assetLink !== 'home') {
                    const prop = factFind.assets?.investmentProperty?.find((p) => String(p.id) === String(liability.assetLink));
                    const rate = liability.interestRate || 0;
                    const interest = balance * (rate / 100 || 0);
                    if (prop && interest > 0) {
                        recordIncomeItem(pool, `Loan Interest (${prop.description || 'property'})`, 0, interest, 0, 0);
                    }
                }
            });
        });
    });

    const funeralBondExemptLimit = isPartnered ? FUNERAL_BOND_EXEMPT_LIMIT_PER_PERSON * 2 : FUNERAL_BOND_EXEMPT_LIMIT_PER_PERSON;

    const result: any = {
        assessableAssets: 0,
        assessableAssetsNoHome: 0,
        financialAssetsForDeeming: 0,
        investmentPropertyRentalForPension: 0,
        partnerInvestmentPropertyRentalForPension: 0,
        clientDistributionMeansTestIncome: 0,
        partnerDistributionMeansTestIncome: 0,
        assessableIncomeFromPensions: 0,
        partnerAssessableIncomeFromPensions: 0,
        investmentIncomeCash: 0,
        entityPrincipalHomeExempt: principalHomeExempt,
        rentalExpenses: 0,
        loanRepaymentsAnnual: 0,
        applicantsLiquidAssets: 0,
        clientTax: { investmentSources: [] as any[], deductionSources: [] as any[], frankingCredits: 0 },
        partnerTax: { investmentSources: [] as any[], deductionSources: [] as any[], frankingCredits: 0 },
        structureLines: [] as any[],
    };

    let funeralBondExemptUsed = 0;

    structures.forEach((structure) => {
        const combinedPct = getApplicantsCombinedApportionmentPercent(structure, !!isPartnered);
        if (combinedPct <= 0) return;

        const clientPct = Math.min(100, Math.max(0, structure.apportionmentClient || 0));
        const partnerPct = isPartnered ? Math.min(100, Math.max(0, structure.apportionmentPartner || 0)) : 0;

        const pool = pools[String(structure.id)] || emptyPool();
        const structureName = structure.name?.trim() || 'Trust/company';

        let assessable = Math.max(0, pool.assessableAssets - pool.liabilitiesGross);
        let assessableNoHome = Math.max(0, pool.assessableAssetsNoHome - pool.liabilitiesNoHome);

        if (pool.funeralBondGross > 0) {
            const remainingExempt = Math.max(0, funeralBondExemptLimit - funeralBondExemptUsed);
            const exemptPortion = Math.min(pool.funeralBondGross, remainingExempt);
            const assessableFuneral = Math.max(0, pool.funeralBondGross - exemptPortion);
            funeralBondExemptUsed += exemptPortion;
            assessable += assessableFuneral;
            assessableNoHome += assessableFuneral;
        }

        const clientShare = (pct: number) => (amount: number) => amount * (pct / 100);

        const clientAssets = clientShare(clientPct)(assessable);
        const partnerAssets = clientShare(partnerPct)(assessable);
        const clientAssetsNoHome = clientShare(clientPct)(assessableNoHome);
        const partnerAssetsNoHome = clientShare(partnerPct)(assessableNoHome);
        const clientFinancial = clientShare(clientPct)(pool.financialForDeeming);
        const partnerFinancial = clientShare(partnerPct)(pool.financialForDeeming);
        const distributionOverride = structure.annualDistributionPa || 0;
        const useDistForMeansTest = distributionOverride > 0 && combinedPct > 0 && structure.useDistributionForMeansTest !== false;

        const clientRental = useDistForMeansTest ? 0 : clientShare(clientPct)(pool.investmentPropertyRental);
        const partnerRental = useDistForMeansTest ? 0 : clientShare(partnerPct)(pool.investmentPropertyRental);
        const clientPensionInc = clientShare(clientPct)(pool.assessableIncomeFromPensions);
        const partnerPensionInc = clientShare(partnerPct)(pool.assessableIncomeFromPensions);
        result.assessableAssets += clientAssets + partnerAssets;
        result.assessableAssetsNoHome += clientAssetsNoHome + partnerAssetsNoHome;
        result.financialAssetsForDeeming += clientFinancial + partnerFinancial;
        result.investmentPropertyRentalForPension += clientRental;
        result.partnerInvestmentPropertyRentalForPension += partnerRental;
        result.assessableIncomeFromPensions += clientPensionInc;
        result.partnerAssessableIncomeFromPensions += partnerPensionInc;

        if (distributionOverride > 0 && combinedPct > 0) {
            const clientDist = distributionOverride * (clientPct / combinedPct);
            const partnerDist = distributionOverride * (partnerPct / combinedPct);
            result.investmentIncomeCash += clientDist + partnerDist;
            if (useDistForMeansTest) {
                result.clientDistributionMeansTestIncome += clientDist;
                result.partnerDistributionMeansTestIncome += partnerDist;
            }
        } else {
            result.investmentIncomeCash += clientShare(clientPct)(pool.investmentIncomeCash);
            result.investmentIncomeCash += clientShare(partnerPct)(pool.investmentIncomeCash);
            result.rentalExpenses += clientShare(clientPct)(pool.rentalExpenses);
            result.rentalExpenses += clientShare(partnerPct)(pool.rentalExpenses);
        }

        result.loanRepaymentsAnnual += clientShare(clientPct)(pool.loanRepaymentsAnnual);
        result.loanRepaymentsAnnual += clientShare(partnerPct)(pool.loanRepaymentsAnnual);

        const netLiquid = Math.max(0, pool.liquidAssetsGross - pool.liabilitiesGross);
        result.applicantsLiquidAssets += clientShare(clientPct)(netLiquid);
        result.applicantsLiquidAssets += clientShare(partnerPct)(netLiquid);

        const applyIncomeToPerson = (personTax: any, pct: number) => {
            pool.incomeItems.forEach((item) => {
                const share = item.taxable * (pct / 100);
                const dedShare = item.deductions * (pct / 100);
                const frankShare = item.franking * (pct / 100);
                if (share > 0) {
                    personTax.investmentSources.push({ name: `${item.label} (${structureName})`, amount: share });
                }
                if (dedShare > 0) {
                    personTax.deductionSources.push({ name: `${item.label} Expenses (${structureName})`, amount: dedShare });
                }
                personTax.frankingCredits += frankShare;
            });
        };

        applyIncomeToPerson(result.clientTax, clientPct);
        if (isPartnered) applyIncomeToPerson(result.partnerTax, partnerPct);

        if (clientAssets + partnerAssets > 0 || pool.liabilitiesGross > 0) {
            result.structureLines.push({
                label: `  - ${structureName} (apportioned net assets)`,
                clientAmount: clientAssets,
                partnerAmount: partnerAssets,
            });
        }
    });

    const hasAny =
        result.assessableAssets > 0 ||
        result.financialAssetsForDeeming > 0 ||
        result.investmentIncomeCash > 0 ||
        result.investmentPropertyRentalForPension > 0 ||
        result.loanRepaymentsAnnual > 0;

    return hasAny || result.clientTax.investmentSources.length > 0 ? result : null;
}

/** Skip entity-owned rows in personal calculateFinancials loops. */
export function shouldSkipPersonalAssetProcessing(ownership: string | undefined): boolean {
    return isEntityOwnership(ownership);
}
